import type { DashboardDefinition } from 'common/types/dashboard/dashboardDefinition';
import {
  ColType,
  ColumnWidthEntry,
  SpreadsheetDefinition,
  SummaryDefinition,
  SummaryViewKind,
  SummaryVisibility,
} from 'common/types/summaryDefinitions';

export const SUMMARY_TYPE_FIRM = 'firm';
export const SUMMARY_TYPE_FUND = 'fund';
export const SUMMARY_TYPE_VALUATIONS = 'valuations';
export type SummaryType = typeof SUMMARY_TYPE_FIRM | typeof SUMMARY_TYPE_FUND | typeof SUMMARY_TYPE_VALUATIONS;

export interface OrderedDefinition extends SummaryDefinition {
  isGlobal: boolean;
}

export type ColumnWidth = ColumnWidthEntry;

export interface SummaryDefinitionsState {
  // Definition data
  definitions: SummaryDefinition[];
  globalDefinitions: SummaryDefinition[];
  order: number[];

  // Derived/computed
  orderedDefinitions: OrderedDefinition[];

  // Active definition
  activeDefinitionId: number | null;
  activeDefinition: SummaryDefinition | null;

  // Edit state
  hasUnsavedChanges: boolean;
  pendingChanges: Partial<SpreadsheetDefinition> | null;

  // Column widths (keyed by definitionId, persisted to server)
  columnWidths: Record<number, ColumnWidth[]>;
  activeColumnWidths: ColumnWidth[];

  // Loading states
  isLoading: boolean;
  // True while any fetch (including a background revalidation over cached data) is in flight.
  isFetching: boolean;
  // True once the definitions query has completed at least one fetch.
  isFetched: boolean;
  isSaving: boolean;
  error: Error | null;
}

export interface SummaryDefinitionsActions {
  // Definition CRUD
  createDefinition: (
    name: string,
    spreadsheetDefinition?: SpreadsheetDefinition,
    visibility?: SummaryVisibility,
    clonedFrom?: number | null
  ) => Promise<SummaryDefinition>;
  // Dashboard views are created separately: they send no spreadsheet payload, so folding them into
  // createDefinition would mean branching the spreadsheet normalization path for every caller.
  createDashboardDefinition: (
    name: string,
    dashboardDefinition?: DashboardDefinition,
    visibility?: SummaryVisibility,
    clonedFrom?: number | null
  ) => Promise<SummaryDefinition>;
  updateDefinition: (
    definitionId: number,
    updates: Partial<{
      name: string;
      spreadsheet_definition: SpreadsheetDefinition;
      view_kind: SummaryViewKind;
      dashboard_definition: DashboardDefinition;
      visibility: SummaryVisibility;
    }>
  ) => Promise<SummaryDefinition>;
  deleteDefinition: (definitionId: number) => Promise<void>;
  cloneDefinition: (sourceDefinitionId: number, newName: string) => Promise<SummaryDefinition>;

  // Tab ordering
  updateOrder: (newOrder: number[]) => Promise<void>;
  moveDefinition: (definitionId: number, direction: 'up' | 'down') => Promise<void>;

  // Active definition management
  setActiveDefinition: (definitionId: number | null) => void;

  // Column management for active definition
  addColumn: (columnType: ColType, index?: number) => void;
  removeColumn: (columnType: ColType) => void;
  reorderColumns: (newColumns: Record<number, ColType>) => void;

  // Column width management
  setColumnWidth: (column: string, width: number) => void;
  resetColumnWidths: () => void;

  // Immediate column order persistence (bypasses pendingChanges)
  setColumnOrder: (columns: Record<number, ColType>) => Promise<void>;

  // Sort/filter management
  setSortKeys: (sortKeys: SpreadsheetDefinition['sortKeys']) => void;
  setGroupBy: (groupBy: SpreadsheetDefinition['groupBy']) => void;
  setFilters: (filters: SpreadsheetDefinition['filters']) => void;

  // Change management
  saveChanges: () => Promise<void>;
  discardChanges: () => void;

  // Refresh
  refetch: () => Promise<void>;
}

export interface SummaryDefinitionsContextValue extends SummaryDefinitionsState, SummaryDefinitionsActions {}

export interface SummaryDefinitionsProviderProps {
  children: React.ReactNode;
  summaryType: SummaryType;
  entityId: number; // firmId or fundId
  initialActiveDefinitionId?: number | null;
  fetchEnabled?: boolean;
}
